import { X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

type Props = {
  tone: 'danger' | 'warn';
  icon: LucideIcon;
  title: ReactNode;
  body?: ReactNode;
  confirmLabel: string;
  confirmIcon?: LucideIcon;
  onConfirm: () => void;
  onCancel: () => void;
  confirmDisabled?: boolean;
  pending?: boolean;
  children?: ReactNode;
};

export function ConfirmModal({
  tone,
  icon: Icon,
  title,
  body,
  confirmLabel,
  confirmIcon: ConfirmIcon,
  onConfirm,
  onCancel,
  confirmDisabled = false,
  pending = false,
  children,
}: Props) {
  const isDanger = tone === 'danger';

  return (
    <>
      {/* Backdrop — blur kept, click outside closes */}
      <div
        className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
        onClick={onCancel}
      />
      {/* Centering wrapper — pointer-events-none so backdrop clicks still reach the backdrop */}
      <div className="pointer-events-none fixed inset-0 z-[80] flex items-center justify-center p-4">
        <div
          className={cn(
            'pointer-events-auto w-full max-w-sm border bg-iron-950 p-5 shadow-forged',
            isDanger ? 'border-red-500/40' : 'border-amber-500/40'
          )}
        >
          <div className="flex items-start gap-3">
            <div
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center border',
                isDanger
                  ? 'border-red-500/40 bg-red-500/10'
                  : 'border-amber-500/40 bg-amber-500/10'
              )}
            >
              <Icon
                className={cn('h-4 w-4', isDanger ? 'text-red-300' : 'text-amber-300')}
                strokeWidth={2.5}
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-black uppercase tracking-wide text-white">
                {title}
              </p>
              {body && <p className="mt-1 text-xs text-iron-400">{body}</p>}
            </div>
            <button
              onClick={onCancel}
              className="flex h-7 w-7 shrink-0 items-center justify-center border border-iron-800 text-iron-500 transition-colors hover:border-iron-600 hover:text-iron-300"
              aria-label="Cancel"
            >
              <X className="h-3 w-3" strokeWidth={2.5} />
            </button>
          </div>

          {children && <div className="mt-4">{children}</div>}

          <div className="mt-4 flex gap-2">
            <button
              onClick={onCancel}
              className="flex-1 border border-iron-700 bg-iron-950 px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider text-iron-200 transition-colors hover:border-iron-500 hover:bg-iron-900 hover:text-white"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={confirmDisabled || pending}
              className={cn(
                'flex-1 border px-3 py-2 font-display text-[11px] font-black uppercase tracking-wider transition-all',
                isDanger
                  ? 'border-red-500/60 bg-red-950/60 text-red-100 hover:border-red-400 hover:bg-red-900/70 hover:text-white disabled:opacity-50'
                  : 'border-amber-500/60 bg-amber-950/60 text-amber-100 hover:border-amber-400 hover:bg-amber-900/70 hover:text-white disabled:cursor-not-allowed disabled:opacity-40'
              )}
            >
              {ConfirmIcon && <ConfirmIcon className="mr-1 inline h-3 w-3" strokeWidth={3} />}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
